/**
 * Preset tag library keyed by category slug.
 *
 * - Tags are used to make venues more discoverable on the guest map.
 *   When a guest searches "ice cream" or "souvlaki", venues that have
 *   those tags should pop up.
 * - Tags are NEVER displayed on the guest map cards — they are pure
 *   search hints — but they are editable by the venue owner in Card
 *   Settings (and via the free-text "extra tags" input for things
 *   that aren't in our library).
 * - Custom tags are stored verbatim (lower-cased and trimmed) so the
 *   search matching is straightforward.
 */
export const VENUE_TAGS_BY_CATEGORY = {
  'breakfast-coffee': [
    'pancakes', 'waffles', 'eggs benedict', 'omelette', 'french toast',
    'croissant', 'bagel', 'smoothie', 'coffee', 'espresso', 'latte',
    'cappuccino', 'avocado toast', 'porridge', 'granola',
  ],
  brunch: [
    'avocado toast', 'mimosa', 'eggs benedict', 'pancakes', 'smoothie bowl',
    'granola', 'brioche', 'bloody mary', 'french toast', 'shakshuka',
    'eggs royale', 'huevos rancheros',
  ],
  'street-food': [
    'hot dog', 'burger', 'cheeseburger', 'souvlaki', 'gyros', 'pita',
    'tacos', 'burritos', 'falafel', 'french fries', 'kebab', 'shawarma',
    'hot sandwich', 'crepes', 'wraps', 'loaded fries', 'chicken wings',
    'pizza slice',
  ],
  grill: [
    'bbq', 'ribs', 'steak', 'mixed grill', 'grilled chicken', 'kebab',
    'burger', 'smoked meat', 'brisket', 'lamb chops', 'skewers', 'gyros',
  ],
  'fine-dining': [
    'tasting menu', 'fine dining', 'truffle', 'foie gras', 'caviar',
    'wagyu', 'oysters', 'wine pairing', 'chef’s table', 'degustation',
    'michelin', 'risotto', 'duck confit',
  ],
  'cocktails-bars': [
    'martini', 'margarita', 'mojito', 'whiskey', 'gin', 'craft beer',
    'signature cocktails', 'shots', 'aperitivo', 'spritz', 'negroni',
    'champagne', 'tequila', 'rum',
  ],
  'wine-bars': [
    'red wine', 'white wine', 'rosé', 'natural wine', 'sparkling', 'prosecco',
    'champagne', 'wine flights', 'wine pairing', 'sommelier', 'cheese board',
    'charcuterie',
  ],
  mediterranean: [
    'greek salad', 'moussaka', 'souvlaki', 'gyros', 'tzatziki', 'feta',
    'octopus', 'grilled fish', 'sea bass', 'mezze', 'hummus', 'olives',
    'paella', 'tapas', 'seafood',
  ],
  italian: [
    'pasta', 'pizza', 'risotto', 'carbonara', 'lasagna', 'tiramisu',
    'gnocchi', 'bruschetta', 'antipasti', 'ravioli', 'prosciutto',
    'aperol spritz', 'espresso',
  ],
  indian: [
    'curry', 'butter chicken', 'tikka masala', 'biryani', 'naan',
    'samosa', 'tandoori', 'dal', 'paneer', 'vindaloo', 'chai',
    'lassi', 'chutney',
  ],
  asian: [
    'sushi', 'ramen', 'dim sum', 'pho', 'pad thai', 'dumplings',
    'bubble tea', 'teriyaki', 'kimchi', 'tempura', 'hot pot', 'bao buns',
    'sashimi', 'gyoza', 'miso soup', 'spring rolls', 'bibimbap',
  ],
  mexican: [
    'tacos', 'burritos', 'quesadilla', 'enchiladas', 'nachos', 'guacamole',
    'salsa', 'ceviche', 'tostadas', 'chimichanga', 'fajitas', 'margarita',
    'churros',
  ],
  'comfort-food': [
    'stew', 'casserole', 'meatballs', 'mac and cheese', 'shepherd’s pie',
    'roast', 'lasagna', 'burek', 'cevapi', 'sarma', 'goulash',
    'home-style', 'magirefta', 'soup',
  ],
  desserts: [
    'ice cream', 'gelato', 'cake', 'chocolate', 'cheesecake', 'tiramisu',
    'baklava', 'donuts', 'crepes', 'sorbet', 'churros', 'macarons',
    'cookies', 'brownies', 'waffles', 'pastry',
  ],
  'vegetarian-vegan': [
    'vegan burger', 'vegan pizza', 'salad bowl', 'buddha bowl', 'tofu',
    'tempeh', 'seitan', 'plant-based', 'gluten-free', 'quinoa',
    'falafel', 'hummus', 'smoothie',
  ],
};

/**
 * Returns the union of preset tags for the supplied category slugs.
 * Unknown (custom) categories contribute no preset tags. Output is
 * de-duplicated while preserving first-seen order.
 */
export function presetTagsForCategories(categorySlugs) {
  if (!Array.isArray(categorySlugs)) return [];
  const seen = new Set();
  const result = [];
  categorySlugs.forEach((slug) => {
    const list = VENUE_TAGS_BY_CATEGORY[slug] || [];
    list.forEach((tag) => {
      if (!seen.has(tag)) {
        seen.add(tag);
        result.push(tag);
      }
    });
  });
  return result;
}

/**
 * Normalise a free-typed tag: trim, collapse internal whitespace,
 * lowercase (tags are case-insensitive for search). Returns empty
 * string if the input is blank.
 */
export function normalizeTag(input) {
  if (!input) return '';
  return String(input).trim().replace(/\s+/g, ' ').toLowerCase();
}
